import { Grid, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  documentShortDescriptions,
  documentLongDescriptions
} from 'common/genericDescriptions';
import { BooleanValue, Option, laqusUserUploadDocumentsOptions, uploadDocumentsOptions } from 'common/genericOptions';
import {
  DocumentInfo,
  GrupoAcesso,
  LaqusUserUploadDocumentType,
  TipoDistribuicaoPublica,
  TipoRegistroInstrumentoFinanceiro,
  UploadDocumentType,
} from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Dropdown, Section } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { RootState } from 'store';
import { updateDocuments, updateDocumentsToDelete } from 'store/modules/cra/uploadDocumentos';
import { v4 as uuid } from 'uuid';

const mapStateToProps = (state: RootState) => ({
  distribuicaoPublica: state.craCadastro.caracteristicas.distribuicaoPublica,
  tipoDistribuicaoPublica: state.craCadastro.caracteristicas.tipoDistribuicaoPublica,
  uploadDocumentos: state.craCadastro.uploadDocumentos,
  typeOfRegister: state.craCadastro.typeOfRegister,
});

const mapDispatchToProps = { updateDocuments, updateDocumentsToDelete };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const Documentos: React.FC<Props> = (props) => {
  const {
    uploadDocumentos: { documents, documentsToDelete },
    updateDocuments,
    updateDocumentsToDelete,
    distribuicaoPublica,
    tipoDistribuicaoPublica,
    typeOfRegister,
  } = props;

  const isUsuarioLaqus = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso !== GrupoAcesso.Comum
  );

  const multipleDocTypes = [LaqusUserUploadDocumentType.BoletimSubscricao as string];

  function isDocumentOptional(option: Option): boolean {
    return Object.values(LaqusUserUploadDocumentType).includes(option.value as LaqusUserUploadDocumentType);
  }

  const [uploadDocumentType, setUploadDocumentType] = useState<string>('');

  const getDocumentOptions = () => {
    const options = [];
    if (isUsuarioLaqus || distribuicaoPublica.value === BooleanValue.Yes) {
      options.push(...laqusUserUploadDocumentsOptions);
    }
    if (distribuicaoPublica.value === BooleanValue.No) {
      const termoSecuritizacaoDoc: Option = {
        value: UploadDocumentType.TermoSecuritizacao,
        description: documentShortDescriptions[UploadDocumentType.TermoSecuritizacao],
      };
      options.push(termoSecuritizacaoDoc);
    }
    if (tipoDistribuicaoPublica.value === TipoDistribuicaoPublica._400) {
      options.push(...uploadDocumentsOptions);
    }
    if (tipoDistribuicaoPublica.value === TipoDistribuicaoPublica._476) {
      options.push(...uploadDocumentsOptions.filter(
        (option) =>
          option.value !== UploadDocumentType.OficioConcessaoRegistroOfertaPublica &&
          option.value !== UploadDocumentType.ProspectoPreliminarDefinitivo
      ));
    }
    return options;
  };
  const documentOptions = getDocumentOptions();

  useEffect(() => {
    if (!documents.length) {
      updateDocuments(
        documentOptions?.map((option) => ({
          id: option.value,
          type: option.value,
          optional: isDocumentOptional(option)
        }))
      );
    }
  }, [documents]);

  useEffect(() => {
    if (documents.length) {
      const newestDocuments = [...documents]
        .sort((doc1, doc2) => {
          const date1 = doc1.remoteFileInfo?.creationDate ? new Date(doc1.remoteFileInfo.creationDate).getTime() : 0;
          const date2 = doc2.remoteFileInfo?.creationDate ? new Date(doc2.remoteFileInfo.creationDate).getTime() : 0;
          return date2 - date1;
      })
      .reduce((acc: DocumentInfo[], doc) => {
        if (!acc.some(d => d.type === doc.type)) {
          acc.push(doc);
        }
        return acc;
      }, []);
      const newUploadDocuments = documentOptions.map((option) => {
        const optional = isDocumentOptional(option);
        const document = newestDocuments.find((document) => document.type === option.value);
        return document ? { ...document, optional } : { type: option.value, optional };
      });
      updateDocuments(newUploadDocuments);
      const documentsToDelete: DocumentInfo[] = [];
      documents.forEach((document) => {
        if (!documentOptions.some((option) => option.value === document.type)) {
          documentsToDelete.push(document);
        }
      });
      updateDocumentsToDelete(documentsToDelete.concat(documentsToDelete));
    }
  }, []);

  const handleUpdateFile = (attachments: Attachment[]) => {
    let updatedDocuments = [...documents];

    attachments.forEach(attachment => {
      const docMatchingTypeIndex = updatedDocuments.findIndex(doc => attachment.id === doc.type);
      const isMultiple = multipleDocTypes.includes(attachment.id)

      if (isMultiple && updatedDocuments[docMatchingTypeIndex].file) {
        const newDoc = {
          ...updatedDocuments[docMatchingTypeIndex],
          file: attachment.file,
          id: attachment.id + uuid()
        };
        updatedDocuments = [...updatedDocuments, newDoc]
      }
      else {
        updatedDocuments = documents.map((doc, index) => {
          return index == docMatchingTypeIndex ? { ...doc, file: attachment.file } : doc;
        });
      }

      updateDocuments(updatedDocuments)
    })
  };

  const handleDeleteFileType = async (type: string) => {
    for (const document of documents) {
      if (document.type === type && !!document.remoteFileInfo) {
        await updateDocumentsToDelete(documentsToDelete.concat({ ...document }));
      }
    }

    let countOfType = documents.filter(document => document.type === type).length;

    var updatedDocs = documents
      .map((doc) => {
        if (doc.type === type) {
          return { ...doc, file: undefined, remoteFileInfo: undefined }
        }
        return doc;
      })
      .filter((documentInfo) => {
        if (documentInfo.type === type && countOfType !== 1) {
          countOfType--;
          return false;
        }
        return true;
      })

    updateDocuments(updatedDocs);
  };



  return (
    <Section title={'Upload de Documentos'}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Dropdown
            label={'Selecione o tipo do documento'}
            required
            options={documentOptions}
            value={uploadDocumentType ?? ''}
            onChange={setUploadDocumentType}
          />
        </Grid>
        <Grid item xs={12}>
          <UploadDocs
            inputAttachments={documents
              .filter((attachment) => !!attachment.remoteFileInfo)
              .map(({ type, remoteFileInfo }) => ({
                id: type,
                remoteFileInfo,
              }))}
            fileId={`${uploadDocumentType}`}
            disabled={!uploadDocumentType}
            onChange={handleUpdateFile}
            onDelete={handleDeleteFileType}
            multipleFiles={uploadDocumentType == LaqusUserUploadDocumentType.BoletimSubscricao}
          />
        </Grid>
        <Grid item xs={12}>
          {documents?.filter((doc, index, self) => index === self.findIndex((t) => t.type === doc.type))
            .filter(doc => !(doc.optional && !doc.file && !doc.remoteFileInfo))
            .map((document) => (
              <React.Fragment key={document.type}>
                <Grid container spacing={1}>
                  <Grid item>
                    <CheckCircleIcon color={document.file || document.remoteFileInfo ? 'primary' : 'disabled'} />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2">{documentLongDescriptions[document.type]}</Typography>
                  </Grid>
                </Grid>
              </React.Fragment>
            ))}
        </Grid>
      </Grid>
    </Section>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Documentos);
